/**
 * DOM layer — attach live numeric formatting to an <input>.
 *
 *   <input data-numkey>                        — integers: 1,234,567
 *   <input data-numkey="2">                    — 2 decimals: 1,234.56
 *   <input data-numkey data-numkey-negative>   — minus allowed
 *   <input data-numkey data-numkey-align="left">  — opt out of right-align
 *   <input data-numkey data-numkey-korean>     — live "150만" reading in a
 *       generated <span class="numkey-korean"> after the input (or give the
 *       attribute a CSS selector to use an existing element)
 *   <input data-numkey data-numkey-name="amount"> — keep a generated hidden
 *       input named "amount" in sync with the CANONICAL value, so a classic
 *       form POST submits "1234567" while the field shows "1,234,567"
 *   <input data-numkey data-numkey-korean-entry> — accept Korean shorthand:
 *       typing "3만5천" is left alone until blur, which converts it to
 *       "35,000" (fromKorean)
 *
 * Binding sets `inputmode` (numeric/decimal) and right-aligns the field
 * unless either is already set, formats any server-rendered value in place,
 * and keeps the caret stable across reformatting (see core caret math).
 * IME-safe: nothing runs mid-composition (Korean IME half-composed jamo in a
 * number field would be destroyed otherwise); conversion runs on
 * `compositionend` and on regular `input` events. Blur settles transient
 * states ("1,234." → "1,234").
 *
 * Use `type="text"` inputs — `type="number"` has no caret API and already
 * fights custom formatting. numkey sets `inputmode` for the mobile keypad.
 */
import {
  caretIndex,
  clamp,
  countSignificant,
  finalize,
  format,
  parse,
  resolveOptions,
  type NumkeyOptions
} from './core'
import { fromKorean, KOREAN_NUMBER_CHARS, toKorean } from './korean'

const SELECTOR = 'input[data-numkey]'

const unbinders = new WeakMap<HTMLInputElement, () => void>()

/**
 * `data-numkey-group` value → the `group` option. A single number is uniform
 * grouping ("4" for 만-style); a `primary,secondary` pair is non-uniform
 * ("3,2" for the Indian lakh system).
 */
function parseGroupAttr(value: string): number | [number, number] {
  const parts = value.split(',').map((n) => parseInt(n.trim(), 10))
  const primary = parts[0]
  if (primary === undefined || Number.isNaN(primary)) return 3
  const secondary = parts[1]
  if (parts.length < 2 || secondary === undefined || Number.isNaN(secondary)) {
    return primary || 3
  }
  return [primary, secondary]
}

/** Read options from `data-numkey*` attributes. */
export function optionsFromElement(el: HTMLInputElement): NumkeyOptions {
  const main = el.getAttribute('data-numkey')
  const group = el.getAttribute('data-numkey-group')
  const separator = el.getAttribute('data-numkey-separator')
  const decimalPoint = el.getAttribute('data-numkey-point')
  const locale = el.getAttribute('data-numkey-locale')
  const min = el.getAttribute('data-numkey-min')
  const max = el.getAttribute('data-numkey-max')
  const opts: NumkeyOptions = {
    decimals: main ? parseInt(main, 10) || 0 : 0,
    negative: el.hasAttribute('data-numkey-negative'),
    koreanEntry: el.hasAttribute('data-numkey-korean-entry')
  }
  if (group) opts.group = parseGroupAttr(group)
  if (separator !== null) opts.separator = separator
  if (decimalPoint !== null) opts.decimalPoint = decimalPoint
  if (locale) opts.locale = locale
  if (min !== null) opts.min = min
  if (max !== null) opts.max = max
  return opts
}

function getCaret(el: HTMLInputElement): number | null {
  try {
    return el.selectionStart
  } catch {
    return null // type="number" etc. — no selection API
  }
}

function setCaret(el: HTMLInputElement, pos: number): void {
  try {
    el.setSelectionRange(pos, pos)
  } catch {
    /* no selection API */
  }
}

/**
 * Reformat an input's value in place, preserving the caret (significant
 * chars before the caret are counted in the old value and the caret lands
 * after the same count in the new display). Returns whether the value
 * changed — the building block the bind layer and framework adapters share.
 */
export function applyToInput(
  el: HTMLInputElement,
  opts?: NumkeyOptions
): boolean {
  const raw = el.value
  const display = format(parse(raw, opts), opts)
  if (display === raw) return false
  const caret = getCaret(el)
  el.value = display
  if (caret !== null) {
    setCaret(el, caretIndex(display, countSignificant(raw.slice(0, caret), opts), opts))
  }
  return true
}

/** Settle transient states, apply min/max, and reformat — what blur runs. */
export function finalizeInput(
  el: HTMLInputElement,
  opts?: NumkeyOptions
): boolean {
  const display = format(clamp(finalize(parse(el.value, opts)), opts), opts)
  if (display === el.value) return false
  el.value = display
  return true
}

/**
 * Caret adjustment that makes Backspace/Delete take out the digit next to a
 * separator in ONE keystroke. Without it, backspacing at "1,|234" deletes
 * the comma, the reformat puts it back, and the caret just drifts left —
 * the digit dies on the second press. Call from keydown BEFORE the default
 * action: the caret is moved past the separator so the default deletes the
 * digit, and the input event reformats as usual.
 */
export function adjustDeleteCaret(
  el: HTMLInputElement,
  key: string,
  opts?: NumkeyOptions
): void {
  if (key !== 'Backspace' && key !== 'Delete') return
  const o = resolveOptions(opts ?? optionsFromElement(el))
  if (o.separator.length !== 1) return
  const start = getCaret(el)
  let end: number | null = null
  try {
    end = el.selectionEnd
  } catch {
    /* no selection API */
  }
  if (start === null || start !== end) return // range selections delete as-is
  if (key === 'Backspace' && start > 0 && el.value[start - 1] === o.separator) {
    setCaret(el, start - 1)
  } else if (key === 'Delete' && el.value[start] === o.separator) {
    setCaret(el, start + 1)
  }
}

/**
 * Clamp a canonical value to the field's `decimals` budget ("1.5" → "1" on
 * an integer field). Needed after `fromKorean`, which can yield fractions
 * the field itself would never allow.
 */
function constrain(canonical: string, o: NumkeyOptions): string {
  const point = canonical.indexOf('.')
  if (point === -1) return canonical
  const decimals = o.decimals ?? 0
  if (decimals <= 0) return canonical.slice(0, point) || '0'
  const cut = canonical.slice(0, point + 1 + decimals)
  return cut.endsWith('.') ? cut.slice(0, -1) : cut
}

/**
 * Whether a value is a Korean-shorthand draft that live reformatting must
 * not touch: it contains Korean number chars, or it is a decimal draft on
 * an integer field ("1." on the way to "1.5억" — stripping the point before
 * the unit arrives would silently turn 1.5억 into 15억).
 */
function isKoreanDraftValue(value: string, o: NumkeyOptions): boolean {
  if (!(o.koreanEntry ?? false)) return false
  if (KOREAN_NUMBER_CHARS.test(value)) return true
  return (o.decimals ?? 0) === 0 && /\d[.．]\d*$/.test(value)
}

/** Canonical value of a raw display value under the given options. */
function canonicalOf(value: string, o: NumkeyOptions): string {
  if (isKoreanDraftValue(value, o)) return constrain(fromKorean(value), o)
  return finalize(parse(value, o))
}

/** The canonical (unformatted, settled) value of a bound input. */
export function getValue(el: HTMLInputElement, opts?: NumkeyOptions): string {
  return canonicalOf(el.value, opts ?? optionsFromElement(el))
}

/**
 * Set an input from a CANONICAL value ("1234567.89") — the write counterpart
 * of `getValue`. Use this for programmatic updates (loading a record,
 * switching display options): assigning a canonical string to `el.value`
 * directly would show it unformatted, and re-parsing it under a non-`.`
 * decimal mark would corrupt it.
 */
export function setValue(
  el: HTMLInputElement,
  canonical: string,
  opts?: NumkeyOptions
): void {
  el.value = format(finalize(canonical), opts ?? optionsFromElement(el))
}

export interface BindOptions {
  /**
   * Re-dispatch a bubbling `input` event after a reformat changes the value.
   * Frameworks that attach their own `input` listener before the binding
   * (Svelte's `bind:value`) read the value before formatting runs; the
   * re-dispatched event lets them pick up the formatted display. Safe
   * against loops — reformatting is idempotent, so the second pass changes
   * nothing and does not re-dispatch.
   */
  resync?: boolean
}

/**
 * Bind formatting to a single element. Options are read from the
 * `data-numkey*` attributes at event time unless `opts` is given explicitly.
 * Returns an unbind function. Binding an already-bound element is a no-op
 * that returns the existing unbinder.
 */
export function bind(
  el: HTMLInputElement,
  opts?: NumkeyOptions,
  bindOpts?: BindOptions
): () => void {
  const existing = unbinders.get(el)
  if (existing) return existing

  const resync = (changed: boolean): void => {
    if (changed && bindOpts?.resync) {
      el.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  const resolve = (): NumkeyOptions => opts ?? optionsFromElement(el)

  // One-time setup: mobile keypad, alignment, server-rendered value.
  const initial = resolve()
  if (!el.hasAttribute('inputmode')) {
    el.setAttribute('inputmode', (initial.decimals ?? 0) > 0 ? 'decimal' : 'numeric')
  }
  if (
    el.style.textAlign === '' &&
    el.getAttribute('data-numkey-align') !== 'left'
  ) {
    el.style.textAlign = 'right'
  }

  // data-numkey-korean: live "150만" reading. Empty value → generate a span
  // right after the input; a value is a selector for an existing element.
  const koreanAttr = el.getAttribute('data-numkey-korean')
  let koreanTarget: Element | null = null
  let koreanCreated = false
  if (koreanAttr !== null) {
    if (koreanAttr === '') {
      koreanTarget = document.createElement('span')
      koreanTarget.className = 'numkey-korean'
      el.insertAdjacentElement('afterend', koreanTarget)
      koreanCreated = true
    } else {
      koreanTarget = document.querySelector(koreanAttr)
    }
  }

  // data-numkey-name: hidden input carrying the canonical value for form
  // POSTs. Always generated (and removed on unbind).
  const hiddenName = el.getAttribute('data-numkey-name')
  let hidden: HTMLInputElement | null = null
  if (hiddenName) {
    hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = hiddenName
    el.insertAdjacentElement('afterend', hidden)
  }

  const syncExtras = (o: NumkeyOptions): void => {
    if (!koreanTarget && !hidden) return
    const canonical = canonicalOf(el.value, o)
    if (koreanTarget) koreanTarget.textContent = toKorean(canonical)
    if (hidden) hidden.value = canonical
  }

  const settle = (o: NumkeyOptions): void => {
    const before = el.value
    if (isKoreanDraftValue(el.value, o)) {
      el.value = format(clamp(constrain(fromKorean(el.value), o), o), o)
    } else {
      finalizeInput(el, o)
    }
    syncExtras(o)
    resync(el.value !== before)
  }

  settle(initial) // server-rendered value → formatted display

  let composing = false

  const run = (): void => {
    const o = resolve()
    let changed = false
    if (!isKoreanDraftValue(el.value, o)) changed = applyToInput(el, o)
    syncExtras(o)
    resync(changed)
  }

  const onCompositionStart = (): void => {
    composing = true
  }
  const onCompositionEnd = (): void => {
    composing = false
    run()
  }
  const onInput = (): void => {
    if (!composing) run()
  }
  const onBlur = (): void => {
    settle(resolve())
  }
  const onKeyDown = (e: KeyboardEvent): void => {
    if (!composing) adjustDeleteCaret(el, e.key, resolve())
  }

  el.addEventListener('compositionstart', onCompositionStart)
  el.addEventListener('compositionend', onCompositionEnd)
  el.addEventListener('input', onInput)
  el.addEventListener('blur', onBlur)
  el.addEventListener('keydown', onKeyDown)

  const unbind = (): void => {
    el.removeEventListener('compositionstart', onCompositionStart)
    el.removeEventListener('compositionend', onCompositionEnd)
    el.removeEventListener('input', onInput)
    el.removeEventListener('blur', onBlur)
    el.removeEventListener('keydown', onKeyDown)
    if (koreanCreated) koreanTarget?.remove()
    hidden?.remove()
    unbinders.delete(el)
  }
  unbinders.set(el, unbind)
  return unbind
}

function isBindable(node: unknown): node is HTMLInputElement {
  return (
    typeof HTMLInputElement !== 'undefined' && node instanceof HTMLInputElement
  )
}

function bindAll(scope: ParentNode): void {
  for (const el of scope.querySelectorAll(SELECTOR)) {
    if (isBindable(el)) bind(el)
  }
}

/**
 * Framework-agnostic ref-callback factory: pass the element to bind, `null`
 * to unbind. This is what the React `useNumkey` hook wraps — usable directly
 * with any library that hands you element refs.
 */
export function createRefBinder(
  opts?: NumkeyOptions
): (el: HTMLInputElement | null) => void {
  let unbind: (() => void) | null = null
  return (el) => {
    unbind?.()
    unbind = el ? bind(el, opts) : null
  }
}

/**
 * Bind every `[data-numkey]` input under `root` (default: `document`) and
 * keep watching for inputs added later or gaining the attribute. Returns a
 * stop function that disconnects the observer (existing bindings stay).
 */
export function observe(root: ParentNode = document): () => void {
  bindAll(root)

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes' && isBindable(record.target)) {
        if (record.target.hasAttribute('data-numkey')) bind(record.target)
        continue
      }
      for (const node of record.addedNodes) {
        if (isBindable(node) && node.matches(SELECTOR)) bind(node)
        else if (node instanceof Element) bindAll(node)
      }
    }
  })

  observer.observe(root as Node, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-numkey']
  })
  return () => observer.disconnect()
}
